import { StrictMode, useEffect, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './styles.css';
import { App } from './app';
import { BrandProvider } from './brand/BrandProvider';
import { PLATFORM_BRAND, type Brand } from './brand/BrandProvider';
import { activeBrand, fetchHostBrand } from './api/brand';
import { USE_MOCKS } from './api/client';
import { bootstrapSession } from './api/auth';
import { ErrorBoundary } from './components/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Gate the app on a one-time silent session restore AND host-brand resolution
 * (real-API mode). The access token lives only in memory, so a hard reload drops
 * it — but the httpOnly refresh cookie survives, and bootstrapSession() trades it
 * for a fresh token before any page fires a request, avoiding the "logged-in but
 * 401/empty" flash. In parallel we resolve the white-label brand for the current
 * host (Decision #21) BEFORE first paint, so a reseller never sees a flash of the
 * Possignin/ITCity identity (a white-label leak). Both fall back gracefully — a
 * failed brand fetch lands on the platform brand. Mock mode renders immediately
 * with the mock brand (no bootstrap, no host fetch).
 */
function SessionGate({ children }: { children: (brand: Brand) => ReactNode }) {
  const [ready, setReady] = useState(USE_MOCKS);
  // Mock mode renders immediately, so seed with the mock brand; real mode shows
  // the gate and overwrites this once fetchHostBrand() resolves.
  const [brand, setBrand] = useState<Brand>(USE_MOCKS ? activeBrand : PLATFORM_BRAND);
  useEffect(() => {
    if (USE_MOCKS) return;
    let alive = true;
    void Promise.all([bootstrapSession(), fetchHostBrand()])
      .then(([, resolvedBrand]) => {
        if (alive) setBrand(resolvedBrand);
      })
      .finally(() => {
        // Always lift the gate — a failed bootstrap/brand fetch must not hang the
        // app; brand stays on its safe PLATFORM_BRAND fallback.
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-white dark:bg-zinc-950 text-sm text-zinc-500 dark:text-zinc-400">
        Restoring your session…
      </div>
    );
  }
  return <>{children(brand)}</>;
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SessionGate>
          {(brand) => (
            <BrandProvider brand={brand}>
              <App />
            </BrandProvider>
          )}
        </SessionGate>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
